import './App.css';

import axios from 'axios';
import Lottie from 'lottie-react';
import React, { useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import teamplanning from './animations/team-planning.json';

import ContactUsPage from './ContactUsPage';
import CookiesBanner from './CookiesBanner';
import DisclaimerAndPolicyPage from './DisclaimerAndPolicyPage';
import FoodTopic from './FoodTopic';
import FoodType from './FoodType';
import HomePage from './HomePage';
import LatestPage from './LatestPage';
import PersonaPage from './PersonaPage';
import ReportPage from './ReportPage';
import SightseeingTopic from './SightseeingTopic';
import StatisticsTopic from './StatisticsTopic';
import TravelPinboardPage from './TravelPinboardPage';
import TravelTopics from './TravelTopics';
import { MaterialIcon } from './Utils/CountryUtility';
import WorldMap from './WorldMap';

interface CookiePreferencesState {
  adsTracking: 'true' | 'false';
  analytics: 'true' | 'false';
  thirdParty: 'true' | 'false';
  consent: 'true' | 'false';
}

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const frontendUrl = process.env.REACT_APP_FRONTEND_URL;

axios.defaults.withCredentials = true;

function getCSRFToken(): string | undefined {
  const csrfToken = document.cookie
    .split('; ')
    .find((row) => row.startsWith('csrftoken='))
    ?.split('=')[1];
  return csrfToken;
}

axios.interceptors.request.use(
  (config) => {
    try {
      const csrfToken = getCSRFToken();
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      }
    } catch (err) {
      console.error('Error fetching CSRF Token:', err);
    }
    return config;
  },
  (error) => Promise.reject(error),
);

function App() {
  const location = useLocation();
  const [activeMenu, setActiveMenu] = useState('/home');
  const [, setSelectedTopicPath] = useState('');
  const [showChevron, setShowChevron] = useState(true);
  const [themeMode, setThemeMode] = useState<'light' | 'dark'>(localStorage.getItem('themeMode') === 'dark' ? 'dark' : 'light');

  const menuItems = [
    { path: '/home', label: 'Home Page' },
    { path: '/worldmap', label: 'World Map' },
    { path: '/travelpinboard', label: 'Pinboard' },
    { path: '/latestposts', label: 'Latest Posts' },
    { path: '/traveltopics', label: 'Travel Topics' },
  ];

  const [preferences, setPreferences] = useState<CookiePreferencesState>({
    adsTracking: 'false',
    analytics: 'false',
    thirdParty: 'false',
    consent: 'false',
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeMode);
    localStorage.setItem('themeMode', themeMode);
  }, [themeMode]);

  useEffect(() => {
    axios
      .get(`${backendUrl}/cookies/get-cookie-user-preferences/`, { withCredentials: true })
      .then((response) => {
        setPreferences(response.data.preferences);
      })
      .catch((error) => console.error('Error fetching preferences:', error));
  }, []);

  useEffect(() => {
    if (!preferences.adsTracking) return;

    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function () {
        window.dataLayer.push(arguments);
      };

    window.gtag('consent', 'default', {
      ad_storage: preferences.adsTracking === 'true' ? 'granted' : 'denied',
      analytics_storage: preferences.analytics === 'true' ? 'granted' : 'denied',
    });

    if (preferences.adsTracking === 'true') {
      const script = document.createElement('script');
      script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-8141845068352110';
      script.async = true;
      script.crossOrigin = 'anonymous';
      document.body.appendChild(script);
    }
  }, [preferences]);

  useEffect(() => {
    axios
      .get(`${backendUrl}/SetCsrf/`)
      .then(() => {})
      .catch((error) => {
        console.error('Error setting CSRF token:', error);
      });
  }, []);

  useEffect(() => {
    let normalizedPath = location.pathname;
    if (location.pathname.startsWith('/traveltopics')) {
      normalizedPath = '/traveltopics';
    }
    if (location.pathname.startsWith('/travelpinboard')) {
      normalizedPath = '/travelpinboard';
    }
    if (normalizedPath === '/' || normalizedPath === '/homepage') {
      normalizedPath = '/home';
    }

    setActiveMenu(normalizedPath);
    localStorage.setItem('activeMenu', normalizedPath);
  }, [location.pathname]);

  const handleMenuClick = (path: string): void => {
    if (path === '/traveltopics') {
      setSelectedTopicPath('');
    }

    setShowChevron(path !== '/disclaimerandpolicypage');
    setActiveMenu(path);
    localStorage.setItem('activeMenu', path);
  };

  function getIconForMenuItem(path: string): string {
    switch (path) {
      case '/home':
        return 'home';
      case '/worldmap':
        return 'travel_explore';
      case '/traveltopics':
        return 'topic';
      case '/travelpinboard':
        return 'push_pin';
      case '/latestposts':
        return 'conveyor_belt';
      default:
        return '';
    }
  }

  function getChevronPosition(path: string): number {
    const itemHeight = 100 / menuItems.length;
    const activeItemIndex = menuItems.findIndex((item) => item.path === path);
    return itemHeight * activeItemIndex;
  }

  return (
    <>
      <div className="App">
        <CookiesBanner />

        <div className="Bar">
          <div className="Logo">
            <Link to="/home" onClick={() => handleMenuClick('/home')}>
              <img className="GlobeAnimation" src={`${frontendUrl}/static/logo.jpg`} title="Logo" alt="Logo" />
            </Link>
          </div>

          <Link to="/home" onClick={() => handleMenuClick('/home')}>
            <div className="WebsiteTitle">LivingOutOfABackpack</div>
          </Link>

          <button
            type="button"
            className="ThemeToggleButton"
            onClick={() => setThemeMode((currentMode) => (currentMode === 'light' ? 'dark' : 'light'))}
            title={`Switch to ${themeMode === 'light' ? 'dark' : 'light'} mode`}
          >
            <span className="material-icons ThemeToggleIcon">{themeMode === 'light' ? 'dark_mode' : 'light_mode'}</span>
            <span className="ThemeToggleText">{themeMode === 'light' ? 'Dark' : 'Light'}</span>
          </button>
        </div>

        <div className="page">
          <div className="side-bar">
            <div className="Menu">
              <nav className="navigation">
                <ul className="Menu-List">
                  {menuItems.map((item) => (
                    <li className="Menu-Item" key={item.path}>
                      <Link
                        to={item.path}
                        className={`button-link ${activeMenu === item.path ? 'active' : ''}`}
                        onClick={() => handleMenuClick(item.path)}
                      >
                        {item.label}
                      </Link>
                      <Link to={item.path} className="icon-side-bar-link">
                        <span className="material-icons icon-side-bar">{getIconForMenuItem(item.path)}</span>
                        <MaterialIcon iconName={getIconForMenuItem(item.path)} />
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>

              {showChevron && (
                <span className="material-icons chevron" style={{ transform: `translateY(${getChevronPosition(activeMenu)}%)` }}>
                  chevron_left
                </span>
              )}
            </div>

            <div className="PolicyButton">
              <Link to="/disclaimerandpolicypage"></Link>
              <Link
                to="/disclaimerandpolicypage"
                className={`PolicyButtonLink ${activeMenu === 'disclaimerandpolicypage' ? 'active' : ''}`}
                onClick={() => handleMenuClick('/disclaimerandpolicypage')}
              >
                Cookie Preferences & Policy
              </Link>
            </div>

            <div className="ContactUsButton">
              <Link to="/contactuspage"></Link>
              <Link
                to="/contactuspage"
                className={`ContactUsButtonLink ${activeMenu === 'contactuspage' ? 'active' : ''}`}
                onClick={() => handleMenuClick('/contactuspage')}
              >
                Contact Us & Copyright
              </Link>
            </div>
          </div>

          <div className="mobile-menu">
            {menuItems.map((item) => (
              <Link
                key={`mobile-menu-${item.path}`}
                to={item.path}
                className={`mobile-menu-item ${activeMenu === item.path ? 'active' : ''}`}
                onClick={() => handleMenuClick(item.path)}
              >
                <MaterialIcon iconName={getIconForMenuItem(item.path)} style={activeMenu === item.path ? { color: 'var(--accent-color)' } : {}} />
                <span>{item.label}</span>
              </Link>
            ))}
          </div>

          <div className="content-container">
            <Routes>
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route path="/homepage" element={<Navigate to="/home" replace />} />
              <Route path="/home" element={<HomePage />} />
              <Route path="/worldmap" element={<WorldMap />} />

              <Route path="/traveltopics" element={<TravelTopics onTopicPathChange={setSelectedTopicPath} />} />
              <Route path="/traveltopics/:topicPath" element={<TravelTopics key={window.location.pathname} onTopicPathChange={setSelectedTopicPath} />} />

              <Route path="/traveltopics/:country/topics/food" element={<FoodTopic />} />
              <Route path="/traveltopics/:country/topics/sightseeing" element={<SightseeingTopic />} />
              <Route path="/traveltopics/:country/topics/statistics" element={<StatisticsTopic />} />

              <Route path="/latestpage" element={<Navigate to="/latestposts" replace />} />
              <Route path="/latestposts" element={<LatestPage />} />
              <Route path="/personas/:personaId" element={<PersonaPage />} />
              <Route path="/foodtypes/:foodTypeId" element={<FoodType />} />
              <Route path="/travelpinboard" element={<TravelPinboardPage />} />
              <Route path="/travelpinboard/:personaId" element={<TravelPinboardPage />} />

              <Route path="/disclaimerandpolicypage" element={<DisclaimerAndPolicyPage />} />
              <Route path="/contactuspage" element={<ContactUsPage />} />
              <Route path="/reportpage" element={<ReportPage />} />
              <Route
                path="*"
                element={
                  <div className="not-found-logo-container">
                    <div className="not-found-logo-card">
                      <h1 className="not-found-title">Invalid url</h1>
                      <p className="not-found-subtext">This page does not exist. Use the quick menu to navigate to a valid page.</p>
                      <Lottie animationData={teamplanning} loop={true} style={{ height: '320px', width: '320px' }} />
                    </div>
                  </div>
                }
              />
            </Routes>
          </div>
        </div>
      </div>
    </>
  );
}

export default App;
